import { useMemo, useCallback, memo } from 'react';
import type { RefObject } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { SDropdownMenu, SConfirmDialog } from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import { useQuotePricingCalculations } from 'pages/quotes/components/quote-items/components/product/hooks/use-quote-pricing-calculations';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import {
  DeleteIconV2,
  DocumentIconV2,
  RefreshIcon,
  MoveIcon,
  FillBagOutlineIcon,
  AddBySKUOutlineIcon,
  AddSPOOutlineIcon,
  AddSectionIcon,
  EditIcon
} from '@setvi/shared/components/sicons';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import {
  useQuoteData,
  useQuoteMutations,
  useQuoteStatusFlags
} from '../../../context';
import { AddNoteDialog } from './add-note-dialog';
import { EditLabelDialog } from './edit-label-dialog';
import { COLUMN_WIDTHS } from '../column-widths';
import { AddBySku } from '../../add-by-sku';
import { AddSPO } from '../../add-spo';
import { AddFromCatalogDialog } from '../../add-from-catalog-dialog';
import { SectionDialog } from '../../add-section-dialog';
import { OptionRow } from './option-row';
import type { OptionRowHandlers } from './option-row';
import { EditOptionDetailsDialog } from './edit-option-details-dialog';
import { ProductInfo } from './product-info';
import { QuantityColumn, SellColumn, CostColumn, GpColumn } from './columns';
import { FirstRowBox, RowContainer, SecondRowBox } from './styled';
import { useOptionVirtualizer } from '../hooks';

// Pre-create icons at module level to avoid recreating on every render
const MENU_ICONS = {
  refresh: <RefreshIcon fontSize="small" />,
  document: <DocumentIconV2 fontSize="small" />,
  edit: <EditIcon fontSize="small" />,
  options: (
    <AccountTreeOutlinedIcon fontSize="small" style={{ color: '#5D7083' }} />
  ),
  catalog: <FillBagOutlineIcon fontSize="small" />,
  sku: <AddBySKUOutlineIcon fontSize="small" />,
  spo: <AddSPOOutlineIcon fontSize="small" />,
  move: <MoveIcon fontSize="small" />,
  addSection: <AddSectionIcon fontSize="small" />,
  delete: <DeleteIconV2 fontSize="small" />
};

const useStyles = makeStyles(() => ({
  rowContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '12px',
    width: 'fit-content',
    minWidth: '100%'
  },
  productContainer: {
    display: 'flex',
    gap: '16px'
  },
  productInfoColumn: {
    minWidth: COLUMN_WIDTHS.productInfo,
    flex: 1
  },
  qtyColumn: {
    width: COLUMN_WIDTHS.qty,
    flexShrink: 0
  },
  unitPriceColumn: {
    width: COLUMN_WIDTHS.unitPrice,
    flexShrink: 0
  },
  gpColumn: {
    width: COLUMN_WIDTHS.gp,
    flexShrink: 0
  },
  totalPriceColumn: {
    width: COLUMN_WIDTHS.totalPrice,
    flexShrink: 0
  },
  actionColumn: {
    width: COLUMN_WIDTHS.action,
    flexShrink: 0
  },
  noteContainer: {
    marginLeft: '80px',
    paddingTop: '12px',
    borderTop: '1px solid #E2E2EA'
  }
}));

const PRODUCT_HEADER_HEIGHT = 130;
const PRODUCT_HEADER_HEIGHT_WITH_NOTE = 146;

interface ProductRowProps {
  product: QuoteProductDTO;
  searchQuery?: string;
  isSectionChild?: boolean;
  onProductClick?: (product: QuoteProductDTO) => void;
  scrollContainerRef?: RefObject<HTMLDivElement>;
  productStartOffset?: number;
}

export const ProductRow = memo(
  ({
    product,
    searchQuery,
    isSectionChild,
    onProductClick,
    scrollContainerRef,
    productStartOffset
  }: ProductRowProps) => {
    const classes = useStyles();
    const { openDialog, closeDialog } = useDialog();

    // Use split contexts for better performance
    // Components using useQuoteMutations won't re-render when quote data changes
    const { quote, currentStatus } = useQuoteData();
    const {
      updateItem,
      removeItem,
      moveItem,
      addProducts,
      refreshSinglePrice,
      addSectionAndMoveItem
    } = useQuoteMutations();
    const { isAddingProducts } = useQuoteStatusFlags();

    const isQuoteEditable =
      !!currentStatus?.isQuoteEditable && !!quote?.acl?.canEditQuote;
    const notEditableTooltip = 'Quote is not editable';

    const productId = product?.id || product?._id;

    // Option virtualization - only enabled when scroll container ref is provided
    const shouldVirtualizeOptions =
      !!scrollContainerRef &&
      productStartOffset !== undefined &&
      (product?.children?.length ?? 0) > 0;

    // Create a stable empty ref for when scrollContainerRef is not provided
    const emptyRef = { current: null } as RefObject<HTMLDivElement>;

    const { virtualItems: optionVirtualItems, totalSize: optionsTotalSize } =
      useOptionVirtualizer({
        options: product?.children || [],
        scrollContainerRef: scrollContainerRef || emptyRef,
        productStartOffset: productStartOffset ?? 0,
        productHeaderHeight: product?.note?.trim()
          ? PRODUCT_HEADER_HEIGHT_WITH_NOTE
          : PRODUCT_HEADER_HEIGHT,
        enabled: shouldVirtualizeOptions
      });

    // Find the parent section ID by checking which section contains this product
    const parentSectionId = useMemo(() => {
      if (!quote?.items) return null;

      for (const item of quote.items) {
        if (item.type === 'section' && 'children' in item) {
          const hasProduct = item.children?.some(
            child => (child.id || child._id) === productId
          );
          if (hasProduct) {
            return item.id || item._id;
          }
        }
      }
      return null;
    }, [quote?.items, productId]);

    // Memoize sections list for "Move to section" submenu
    const availableSections = useMemo(
      () =>
        quote?.items
          ?.filter(item => {
            if (item.type !== 'section') return false;
            const sectionId = item.id || item._id;
            return sectionId !== parentSectionId;
          })
          .map(section => ({
            id: section.id || section._id,
            name: section.name || 'Unnamed Section'
          })) || [],
      [quote?.items, parentSectionId]
    );

    const handlePricingUpdate = useCallback(
      (itemID: string, updates: any) => {
        updateItem.mutate({ itemId: itemID, updates });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const {
      handleQuantityChange,
      handleSellChange,
      handleProfitMarginChange,
      handleProfitMarginPercentageChange
    } = useQuotePricingCalculations({ updatePricing: handlePricingUpdate });

    const handleOpenAddNoteDialog = useCallback(() => {
      openDialog(
        <AddNoteDialog
          itemId={productId}
          itemName={product.name}
          note={product?.note}
          onClose={() => closeDialog(`add-note-${productId}`)}
          onSave={(id, newNote) => {
            updateItem.mutate({ itemId: id, updates: { note: newNote } });
          }}
        />,
        { id: `add-note-${productId}` }
      );
    }, [
      openDialog,
      closeDialog,
      productId,
      product.name,
      product?.note,
      updateItem
    ]);

    const handleLabelDialog = useCallback(() => {
      openDialog(
        <EditLabelDialog
          itemId={productId}
          labelType={product.labelType || null}
          customLabel={product.customLabel}
          onClose={() => closeDialog(`edit-label-${productId}`)}
          onSave={(id, data) => {
            updateItem.mutate({
              itemId: id,
              updates: {
                labelType: data.labelType,
                customLabel: data.customLabel
              }
            });
          }}
        />,
        { id: `edit-label-${productId}` }
      );
    }, [
      openDialog,
      closeDialog,
      productId,
      product.labelType,
      product.customLabel,
      updateItem
    ]);

    const handleAddToNewSection = useCallback(() => {
      openDialog(
        <SectionDialog
          onClose={() => closeDialog(`add-section-move-${productId}`)}
          addSection={params => {
            addSectionAndMoveItem({
              ...params,
              itemId: productId
            });
            closeDialog(`add-section-move-${productId}`);
          }}
        />,
        { id: `add-section-move-${productId}` }
      );
    }, [openDialog, closeDialog, productId, addSectionAndMoveItem]);

    const handleRemoveItem = useCallback(() => {
      openDialog(
        <SConfirmDialog
          type="delete"
          title="Remove Item from the Quote"
          description="This will permanently remove this item from the quote."
          additionalMessage="This action CANNOT be undone."
          onClose={() => closeDialog(`remove-item-${productId}`)}
          onConfirm={() => {
            removeItem(productId);
            closeDialog(`remove-item-${productId}`);
          }}
          confirmText="Remove"
          closeText="Cancel"
        />,
        { id: `remove-item-${productId}` }
      );
    }, [openDialog, closeDialog, productId, removeItem]);

    const handleAddFromCatalog = useCallback(() => {
      openDialog(
        <AddFromCatalogDialog
          onClose={() => closeDialog(`add-from-catalog-${productId}`)}
          parentId={productId}
          quote={quote}
          addProducts={addProducts}
        />,
        { id: `add-from-catalog-${productId}` }
      );
    }, [openDialog, closeDialog, productId, quote, addProducts]);

    const handleAddBySku = useCallback(() => {
      openDialog(
        <AddBySku
          onClose={() => closeDialog(`add-by-sku-${productId}`)}
          addProducts={products =>
            addProducts({
              products: products.products,
              parentId: productId
            })
          }
          isAddingProducts={isAddingProducts}
        />,
        { id: `add-by-sku-${productId}` }
      );
    }, [openDialog, closeDialog, productId, addProducts, isAddingProducts]);

    const handleAddSpo = useCallback(() => {
      openDialog(
        <AddSPO
          onClose={() => closeDialog(`add-spo-${productId}`)}
          onSubmit={async spoProduct => {
            await addProducts({
              products: [spoProduct as any],
              parentId: productId
            });
            closeDialog(`add-spo-${productId}`);
          }}
          isAddingProducts={isAddingProducts}
        />,
        { id: `add-spo-${productId}` }
      );
    }, [openDialog, closeDialog, productId, addProducts, isAddingProducts]);

    const handleRefreshPrice = useCallback(() => {
      if (product?.sku) {
        refreshSinglePrice({
          productId,
          sku: product.sku
        });
      }
    }, [product?.sku, productId, refreshSinglePrice]);

    // Option row handlers - created once and passed down to avoid context subscriptions in each option
    const handleEditOption = useCallback(
      (optionProduct: QuoteProductDTO) => {
        const optionId = optionProduct?.id || optionProduct?._id;
        openDialog(
          <EditOptionDetailsDialog
            itemId={optionId}
            itemName={optionProduct.name}
            note={optionProduct?.note}
            labelType={optionProduct?.labelType}
            customLabel={optionProduct?.customLabel}
            includeInTotalCost={optionProduct?.includeInTotalCost}
            onClose={() => closeDialog(`edit-option-${optionId}`)}
            onSave={(id, data) => {
              updateItem.mutate({
                itemId: id,
                updates: data
              });
            }}
          />,
          { id: `edit-option-${optionId}` }
        );
      },
      [openDialog, closeDialog, updateItem]
    );

    const handleRemoveOption = useCallback(
      (optionId: string) => {
        openDialog(
          <SConfirmDialog
            type="delete"
            title="Remove Option from the Quote"
            description="This will permanently remove this option from the quote."
            additionalMessage="This action CANNOT be undone."
            onClose={() => closeDialog(`remove-option-${optionId}`)}
            onConfirm={() => {
              removeItem(optionId);
              closeDialog(`remove-option-${optionId}`);
            }}
            confirmText="Remove"
            closeText="Cancel"
          />,
          { id: `remove-option-${optionId}` }
        );
      },
      [openDialog, closeDialog, removeItem]
    );

    // Memoized handlers object for options - avoids creating new objects on each render
    const optionHandlers: OptionRowHandlers = useMemo(
      () => ({
        onQuantityChange: handleQuantityChange,
        onSellChange: handleSellChange,
        onProfitMarginChange: handleProfitMarginChange,
        onProfitMarginPercentageChange: handleProfitMarginPercentageChange,
        onEditOption: handleEditOption,
        onRemoveOption: handleRemoveOption
      }),
      [
        handleQuantityChange,
        handleSellChange,
        handleProfitMarginChange,
        handleProfitMarginPercentageChange,
        handleEditOption,
        handleRemoveOption
      ]
    );

    // Memoize menu items to prevent recreation on every render
    const menuItems = useMemo(() => {
      if (!isQuoteEditable) return [];

      return [
        {
          color: '#333D47',
          icon: MENU_ICONS.refresh,
          text: 'Refresh Price',
          disabled: product?.isFreeHand || !product?.sku,
          onClick: handleRefreshPrice
        },
        {
          color: '#333D47',
          icon: MENU_ICONS.document,
          text: product?.note?.trim() ? 'Edit Note' : 'Add Note',
          onClick: handleOpenAddNoteDialog
        },
        ...(product?.children?.length
          ? [
              {
                color: '#333D47',
                icon: MENU_ICONS.edit,
                text: product?.labelType ? 'Edit Label' : 'Add Label',
                onClick: handleLabelDialog
              }
            ]
          : []),
        {
          color: '#333D47',
          icon: MENU_ICONS.options,
          text: 'Add Options',
          submenu: [
            {
              icon: MENU_ICONS.catalog,
              text: 'Add From Catalog',
              color: '#333D47',
              onClick: handleAddFromCatalog
            },
            {
              icon: MENU_ICONS.sku,
              text: 'Add by SKU',
              color: '#333D47',
              onClick: handleAddBySku
            },
            {
              icon: MENU_ICONS.spo,
              text: 'Add SPO',
              color: '#333D47',
              onClick: handleAddSpo
            }
          ]
        },
        {
          color: '#333D47',
          icon: MENU_ICONS.move,
          text: 'Move to section',
          submenu: [
            ...availableSections.map(section => ({
              text: section.name,
              color: '#333D47',
              onClick: () => moveItem(productId, section.id)
            })),
            {
              icon: MENU_ICONS.addSection,
              text: 'Add to new section',
              color: '#333D47',
              onClick: handleAddToNewSection
            },
            ...(parentSectionId
              ? [
                  {
                    text: 'No section (remove)',
                    color: '#333D47',
                    onClick: () => moveItem(productId, null)
                  }
                ]
              : [])
          ]
        },
        {
          icon: MENU_ICONS.delete,
          color: 'danger',
          text: 'Remove Item',
          onClick: handleRemoveItem
        }
      ];
    }, [
      isQuoteEditable,
      product?.isFreeHand,
      product?.sku,
      product?.note,
      product?.children?.length,
      product?.labelType,
      handleRefreshPrice,
      handleOpenAddNoteDialog,
      handleLabelDialog,
      handleAddFromCatalog,
      handleAddBySku,
      handleAddSpo,
      availableSections,
      moveItem,
      productId,
      handleAddToNewSection,
      parentSectionId,
      handleRemoveItem
    ]);

    return (
      <Box className={classes.rowContainer}>
        <Box className={classes.productContainer}>
          {/* Product Info Column */}
          <Box className={classes.productInfoColumn}>
            <ProductInfo
              badgeText={product?.labelType}
              product={product}
              searchQuery={searchQuery}
              onClick={
                onProductClick ? () => onProductClick(product) : undefined
              }
            />
          </Box>

          {/* Quantity Column */}
          <Box className={classes.qtyColumn}>
            <QuantityColumn
              product={product}
              productId={productId}
              onQuantityChange={handleQuantityChange}
              disabled={!isQuoteEditable}
              disabledTooltip={notEditableTooltip}
            />
          </Box>

          {/* Unit Price Column */}
          <Box className={classes.unitPriceColumn}>
            <SellColumn
              product={product}
              productId={productId}
              onSellChange={handleSellChange}
              disabled={!isQuoteEditable}
              disabledTooltip={notEditableTooltip}
            />
          </Box>

          {/* GP Column */}
          <Box className={classes.gpColumn}>
            <GpColumn
              product={product}
              productId={productId}
              onProfitMarginChange={handleProfitMarginChange}
              onProfitMarginPercentageChange={
                handleProfitMarginPercentageChange
              }
              disabled={!isQuoteEditable}
              disabledTooltip={notEditableTooltip}
            />
          </Box>

          {/* Total Price Column */}
          <Box className={classes.totalPriceColumn}>
            <CostColumn product={product} />
          </Box>

          {/* Action Column */}
          <Box
            className={classes.actionColumn}
            style={
              isSectionChild
                ? { width: `calc(${COLUMN_WIDTHS.action} - 12px)` }
                : undefined
            }>
            <RowContainer>
              <FirstRowBox>
                <SDropdownMenu
                  newDesign
                  disabled={!isQuoteEditable}
                  disabledTooltip={notEditableTooltip}
                  menuItems={menuItems}
                />
              </FirstRowBox>
              <SecondRowBox hasNote={!!product?.note} />
            </RowContainer>
          </Box>
        </Box>

        {/* Child Products (Options) - Virtualized when scroll container is available */}
        {product?.children && product.children.length > 0 && (
          <div
            style={{
              position: shouldVirtualizeOptions ? 'relative' : undefined,
              height: shouldVirtualizeOptions ? optionsTotalSize : undefined
            }}>
            {shouldVirtualizeOptions
              ? optionVirtualItems.map(virtualOption => {
                  const childProduct = product.children[virtualOption.index];
                  return (
                    <div
                      key={childProduct.id || childProduct._id}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualOption.start}px)`
                      }}>
                      <OptionRow
                        isSectionChild={isSectionChild}
                        product={childProduct}
                        searchQuery={searchQuery}
                        onProductClick={onProductClick}
                        isLastChild={
                          virtualOption.index === product.children.length - 1
                        }
                        isQuoteEditable={isQuoteEditable}
                        handlers={optionHandlers}
                      />
                    </div>
                  );
                })
              : product.children.map((childProduct, index) => (
                  <OptionRow
                    isSectionChild={isSectionChild}
                    key={childProduct.id || childProduct._id}
                    product={childProduct}
                    searchQuery={searchQuery}
                    onProductClick={onProductClick}
                    isLastChild={index === product.children.length - 1}
                    isQuoteEditable={isQuoteEditable}
                    handlers={optionHandlers}
                  />
                ))}
          </div>
        )}
      </Box>
    );
  }
);
